import React, { useEffect, useState } from "react";
import { Box, Typography, CircularProgress, Paper, Alert, Button, Backdrop } from "@mui/material";
import SelfServeEntitlementsComponent from "../../components/selfServeEntitlements/selfServeEntitlements";
import { fetchEntitlements, fetchBuddyAccounts, fetchEntitlementDescriptions, createBuddyAccount } from "../../services/selfServeEntitlements";
import BuddyAccountsComponent from "../../components/selfServeEntitlements/buddyAccounts";
import CreateBuddy from "../../components/selfServeEntitlements/createBuddy";
import jwtDecode from "jwt-decode";
import AddEntitlementBundle from "../../components/selfServeEntitlements/addEntitlementBundle";
import SwitchUser from "../../components/selfServeEntitlements/switchUser";



const SelfServeEntitlementsPage = () => {
    const [email, setEmail] = useState("");
    const [entitlements, setEntitlements] = useState([]);
    const [buddyAccounts, setBuddyAccounts] = useState([]);
    const [entitlementDescriptions, setEntitlementDescriptions] = useState([]);
    const [selectedBuddy, setSelectedBuddy] = useState("");

    const [loading, setLoading] = useState(true);
    const [loadingEntitlements, setLoadingEntitlements] = useState(false);

    const [hasPermission, setHasPermission] = useState(false);
    const [userInputEmail, setUserInputEmail] = useState("");

    // Missing modal state added
    const [openModal, setOpenModal] = useState(false);

    const [showSuccess, setShowSuccess] = useState(false);
    const [creatingBuddy, setCreatingBuddy] = useState(false);


    // Load JWT + buddy accounts
    useEffect(() => {
        const token = localStorage.getItem("jwtToken");

        if (token) {
            try {
                const decoded = jwtDecode(token);
                setHasPermission(decoded?.entitlementpermissions?.includes("write") || false);
                setEmail(decoded.email || "");

                fetchEntitlementDescriptions().then(setEntitlementDescriptions);

                fetchBuddyAccounts(decoded.email)
                    .then((accounts) => setBuddyAccounts(accounts || []))
                    .finally(() => setLoading(false));

            } catch (err) {
                console.error("Invalid JWT token", err);
                setLoading(false);
            }
        } else {
            setLoading(false);
        }
    }, []);

    // Fetch entitlements when buddy is selected
    useEffect(() => {
        if (!selectedBuddy) return;

        setLoadingEntitlements(true);
        fetchEntitlements(selectedBuddy)
            .then((data) => setEntitlements(data || {}))
            .finally(() => setLoadingEntitlements(false));
    }, [selectedBuddy]);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
                <CircularProgress />
            </Box>
        );
    }


    return (
        <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 1200, mx: "auto" }}>
            <Typography variant="h4" gutterBottom>
                Self-serve Entitlements
            </Typography>

            <Typography variant="subtitle1" gutterBottom color="text.secondary">
                User: {email}
            </Typography>

            <SwitchUser
                currentEmail={email}
                hasPermission={hasPermission}
                onSwitch={(newEmail) => {
                    setEmail(newEmail);
                    setUserInputEmail(newEmail);
                    setSelectedBuddy("");
                    fetchBuddyAccounts(newEmail).then((data) => setBuddyAccounts(data || []));
                }}
            />

            {/* Buddy Accounts Section */}
            {buddyAccounts.length > 0 ? (
                <Paper sx={{ p: 3, mb: 4 }}>
                    <Typography variant="h6" gutterBottom>
                        Buddy Accounts
                    </Typography>

                    <BuddyAccountsComponent
                        buddyAccounts={buddyAccounts}
                        selectedBuddy={selectedBuddy}
                        setSelectedBuddy={setSelectedBuddy}
                    />


                </Paper>
            ) : (
                <Paper
                    sx={{
                        p: 4,
                        mb: 4,
                        textAlign: "center",
                        borderRadius: 2,
                        border: "1px dashed #90caf9",
                        backgroundColor: "#f0f7ff",
                    }}
                >
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        No buddy accounts found for this user.
                    </Typography>

                    <Button
                        variant="contained"
                        onClick={() => setOpenModal(true)}
                    >
                        Create Buddy Account
                    </Button>
                </Paper>




            )}
            <CreateBuddy
                open={openModal}
                onClose={() => !creatingBuddy && setOpenModal(false)}
                parentEmail={userInputEmail || email}
                onCreate={(data) => {
                    setCreatingBuddy(true);
                    createBuddyAccount(data).then(() => {
                        fetchBuddyAccounts(email).then((acc) => setBuddyAccounts(acc || []));
                        setOpenModal(false);
                        setShowSuccess(true);
                        // auto-hide after 20 seconds
                        setTimeout(() => setShowSuccess(false), 20000);
                    }).finally(() => setCreatingBuddy(false));
                }}
            />



            {/* Add Entitlement Bundle */}
            {selectedBuddy && (
                <Paper sx={{ p: 3, mb: 4, textAlign: "center" }}>
                    <AddEntitlementBundle
                        companyId={selectedBuddy}
                        subscription={entitlements?.subscription}
                        entitlementDescriptions={entitlementDescriptions}
                        onBundleAdded={() =>
                            fetchEntitlements(selectedBuddy).then(setEntitlements)
                        }
                    />
                </Paper>
            )}

            {/* Entitlements Section */}
            {selectedBuddy && (
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Entitlements & Bundles for Selected Buddy Account
                    </Typography>

                    <Alert
                        severity="info"
                        sx={{
                            mb: 3,
                            maxWidth: 900,
                            borderRadius: 2,
                            backgroundColor: "#f0f7ff",
                            color: "#084298",
                        }}
                    >
                        These are the entitlements and bundles currently assigned.
                        To add more, use the <strong>Add Entitlement Bundle</strong> button above.
                    </Alert>

                    {loadingEntitlements ? (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : entitlements?.subscription ? (
                        <SelfServeEntitlementsComponent
                            subscription={entitlements.subscription}
                            companyId={selectedBuddy}
                            entitlementDescriptions={entitlementDescriptions}
                            onRefresh={() =>
                                fetchEntitlements(selectedBuddy).then(setEntitlements)
                            }
                        />
                    ) : (
                        <Typography color="text.secondary">No entitlements found.</Typography>
                    )}
                </Paper>
            )}
            {
                showSuccess && (
                    <Alert
                        severity="success"
                        sx={{
                            mb: 3,
                            borderRadius: 2,
                            fontSize: "1rem",
                            backgroundColor: "#e8f5e9",
                            border: "1px solid #a5d6a7",
                            color: "#2e7d32",
                        }}
                    >
                        Your buddy account has been created with the default entitlements.
                        If you’d like to add additional bundles, you can do so using the
                        <strong> Self-Serve Entitlements </strong> feature.
                    </Alert>
                )
            }
            {creatingBuddy && (
                <Backdrop
                    open={true}
                    sx={{ zIndex: 9999, color: "#fff" }}
                >
                    <CircularProgress color="inherit" />
                    <Typography sx={{ ml: 2 }}>Creating buddy account...</Typography>
                </Backdrop>
            )}

        </Box>

    );

};



export default SelfServeEntitlementsPage;
